import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { Spinner } from 'reactstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import api from '../../helpers/api';
import { applyLoginSuccess } from '../../helpers/authSession';

// Landing page for the SSO handshake. The backend callback sets the auth cookies
// and 302-redirects here with NO response body, so we fetch the session profile
// from /auth/me to hydrate `authUser`, then route by role — exactly like a
// password login. A failed handshake arrives as ?error=<code> and bounces back
// to /login with a localized message.
const SSOCallback = (props) => {
  const { t, i18n } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // React 18 StrictMode double-invokes effects in dev; guard the one-shot fetch.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const failToLogin = (message) => {
      navigate('/login', { replace: true, state: { error: message } });
    };

    const error = searchParams.get('error');
    if (error) {
      failToLogin(t('sso.signin_failed', "We couldn't sign you in. Please try again."));
      return;
    }

    (async () => {
      try {
        // Cookies are already set by the backend redirect; this call rides them.
        const { data } = await api.get('auth/me');
        applyLoginSuccess({
          responseData: data.response,
          // Land via a FULL page load, not an SPA navigate. The session was just
          // persisted to localStorage; a client-side navigate races the route
          // guards' first render (usePermission/useProfile read the snapshot before
          // it's there) and bounces a permission-gated landing route to /pages-404.
          // A hard load re-boots the app with the session present — same as the
          // (working) manual navigation. Error/role-gate paths still use SPA navigate.
          navigate: (to) => {
            const path = typeof to === 'string' ? to : (to && to.pathname) || '/';
            window.location.assign(path);
          },
          i18n,
          t,
          // A valid session but disallowed role / rejected org: send back to login.
          onError: failToLogin,
        });
      } catch (e) {
        failToLogin(t('sso.signin_failed', "We couldn't sign you in. Please try again."));
      }
    })();
  }, []);

  return (
    <div className="authentication-bg min-vh-100">
      <div className="bg-overlay"></div>
      <div className="d-flex flex-column min-vh-100 justify-content-center align-items-center">
        <Spinner color="primary" />
        <p className="text-white-50 mt-3">{t('sso.signing_in', 'Signing you in…')}</p>
      </div>
    </div>
  );
};

export default withTranslation()(SSOCallback);

SSOCallback.propTypes = {
  t: PropTypes.func,
  i18n: PropTypes.object,
};
